import { appConfig } from '../config/appConfig';
import { Review } from '../types';

interface ReviewsResponse {
  items: Review[];
  total?: number;
}

interface SyncReviewsResponse {
  success: boolean;
  fetched: number;
  upserted: number;
  message: string;
}

const getAdminToken = (): string => {
  return btoa(`${appConfig.admin.username}:${appConfig.admin.password}`);
};

export interface FetchReviewsOptions {
  rating?: number;
  limit?: number;
}

/**
 * Fetch cached Google reviews from the backend (public).
 */
export const fetchReviews = async (
  options: FetchReviewsOptions = {}
): Promise<Review[]> => {
  try {
    const params = new URLSearchParams();
    if (options.rating !== undefined) {
      params.append('rating', String(options.rating));
    }
    if (options.limit !== undefined) {
      params.append('limit', String(options.limit));
    }

    const url = `/api/reviews${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(url, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: ReviewsResponse = await response.json();
    return (data.items || []).map((item) => ({
      ...item,
      reviewImages: item.reviewImages || [],
    }));
  } catch (error) {
    console.error('Error fetching reviews:', error);
    throw error;
  }
};

/**
 * Sync Google reviews from the third-party provider into Firestore (admin only).
 */
export const syncReviews = async (): Promise<SyncReviewsResponse> => {
  try {
    const response = await fetch('/api/reviews/sync', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAdminToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed. Please check admin credentials.');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.details || errorData.error || `HTTP ${response.status}: ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error('Error syncing reviews:', error);
    throw error;
  }
};
